import React, { useMemo } from 'react';
import { DataGrid, NavigationItem, Button, BodyText } from 'ui-toolkit';
import { IPresentationInternalContact } from '../../../../types/contacts/IInternalContact';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { getOffsetName } from '../../../../utilities/order/getListOfValidTimeZone';
import { ContactProfileContainer } from '../../../../pages/contactProfile/ContactProfileContainer';
import { formatPhoneNumber } from 'utilities/shared/formatPhoneNumber';
import { IRoleData } from '../../../../types/roles/IRole';
import { UUID } from '../../../../types/shared/UUID';
import { usePrefetch } from '../../../../API/administration/AdminUsersAPI';

interface IInternalOrderContactsTableProps {
  data: IPresentationInternalContact[];
  loading: boolean;
  onEditContact: (contact: IPresentationInternalContact) => void;
  roles: IRoleData[];
}

export const PrefetchWrapper: React.FC<{ userId: UUID; children: React.ReactNode }> = ({ userId, children }) => {
  const prefetch = usePrefetch('getAdminUser', { ifOlderThan: 120 });

  return <span onMouseEnter={() => prefetch(userId)}>{children}</span>;
};

export const InternalOrderContactsTable: React.FC<IInternalOrderContactsTableProps> = ({
  data,
  loading,
  onEditContact,
                                                                                         roles,
}) => {
  const columns: IBootstrapTableColumnProps<IPresentationInternalContact>[] = useMemo(
    () => [
      {
        text: 'Role',
        dataField: 'role',
        sort: true,
        // Display role label if found; fall back to role name if role definition is missing
        // (missing roles would indicate a data integrity issue)
        formatter: (cell, row) => {
          if (!row.role) return <>-</>;
          return <>{roles.find((role) => role.name === row.role)?.label ?? row.role}</>;
        },
      },
      {
        text: 'Name',
        dataField: 'firstName',
        sort: true,
        formatter: (cell, row) => <>{`${row.firstName} ${row.lastName}`}</>,
      },
      {
        text: 'External Email',
        dataField: 'externalEmail',
        sort: true,
        formatter: (cell, row) => {
          const externalEmail = row.externalEmail;
          if (!externalEmail) {
            return <>-</>;
          }
          return <NavigationItem href={`mailto:${externalEmail}`} text={externalEmail} isActive isInline />;
        },
      },
      {
        text: 'Phone',
        dataField: 'phoneNumber',
        sort: true,
        formatter: (cell, row) => <>{formatPhoneNumber(row.phoneNumber ?? '')}</>,
      },
      {
        text: 'Phone Extension',
        dataField: 'phoneExtension',
        sort: true,
        formatter: (cell, row) => <>{row.phoneExtension ? `x${row.phoneExtension}` : '-'}</>,
      },
      {
        text: 'Time Zone',
        dataField: 'timeZone',
        sort: true,
        formatter: (cell, row) => <>{getOffsetName(row?.timeZone ?? '') || '-'}</>,
      },
      {
        text: 'Notes',
        dataField: 'notes',
        sort: true,
        formatter: (cell, row) => <BodyText style={{ whiteSpace: 'pre-line' }}>{row.notes}</BodyText>,
      },
      {
        text: 'Options',
        dataField: 'id',
        formatter: (cell, row) => (
          <>
            <Button intent={'ghost'} onClick={() => onEditContact(row)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <PrefetchWrapper userId={row.admin.id}>
              <ContactProfileContainer userId={row.admin.id} userContactData={row} />
            </PrefetchWrapper>
          </>
        ),
      },
    ],
    [onEditContact, roles],
  );

  return <DataGrid columns={columns} keyField={'key'} data={data} loading={loading} />;
};
